<script setup lang="ts">
import {ExtendedUser} from "@/types";
import BaseLayout from "@/layouts/BaseLayout.vue";
import ProfileLayout from "@/Pages/settings/layouts/ProfileLayout.vue";
import TextField from "@/components/forms/TextField.vue";
import {Head, router, useForm} from "@inertiajs/vue3";
import {useDisplay} from "vuetify";
import {route} from "ziggy-js";

interface Props {
    user: ExtendedUser,
}

const props = defineProps<Props>()
defineOptions({
    layout: BaseLayout
})

const { smAndDown } = useDisplay()

const form = useForm({
    full_name: props.user.full_name,
    company: props.user.company,
    website: props.user.website,
    location: props.user.location,
    is_private: props.user.is_private,
})

const submit = () => {
    form.put(route('user-profile-information.update'))
}
</script>

<template>
    <Head title="Edit profile" />
    <profile-layout
        :user="props.user"
    >
        <h1 class="font-weight-medium text-h5 mt-5">
            Public profile
            <default-btn
                size="small"
                class="border font-weight-medium mr-2 float-end"
                rounded="lg"
                @click="router.visit(route('users.show', {user: props.user.name}))"
            >
                Go to your personal profile
            </default-btn>
        </h1>
        <v-divider class="my-5" />
        <v-form @submit.prevent="submit">
            <v-row :class="smAndDown ? 'w-100' : 'w-75'">
                <v-col cols="12">
                    <text-field
                        v-model="form.full_name"
                        label="Name"
                        :errors="form.errors.full_name"
                    />
                    <text-field
                        v-model="form.website"
                        label="URL"
                        :errors="form.errors.website"
                    />
                    <text-field
                        v-model="form.company"
                        label="Institution"
                        :errors="form.errors.company"
                    />
                    <v-divider class="mb-3" />
                    <text-field
                        v-model="form.location"
                        label="Location"
                        :errors="form.errors.location"
                    />
                    <v-divider class="mb-3" />
                    <v-list-item
                        class="pl-0 mb-3"
                        :ripple="false"
                    >
                        <template #prepend>
                            <v-checkbox-btn
                                v-model="form.is_private"
                                color="primary"
                                :hide-details="true"
                                density="compact"
                                type="checkbox"
                                class="mb-8 mr-1"
                            />
                        </template>
                        <v-list-item-title class="font-weight-bold text-body-2">
                            Make profile private and hide from search results
                        </v-list-item-title>
                        <v-list-item-subtitle class="text-caption">
                            Enabling this will hide your profile from search results and prevent other users from viewing your profile.
                        </v-list-item-subtitle>
                    </v-list-item>

                    <submit-btn
                        :loading="form.processing"
                        type="submit"
                    >
                        <span class="font-weight-bold">
                            Update profile
                        </span>
                    </submit-btn>
                    <v-icon
                        v-if="form.wasSuccessful"
                        color="success"
                        class="ml-2"
                    >
                        mdi-check
                    </v-icon>
                </v-col>
            </v-row>
        </v-form>
    </profile-layout>
</template>

<style scoped>
</style>
